import { Bell, Mail, StickyNote, CalendarDays, ListChecks, AlarmClock, Award, User as UserIcon, File } from "lucide-react";
import { EMPTY_EXCLUDE_TYPES, NOTIFY_TYPE_RECORD_KEYS } from "@/react/hooks/entityHooks/useEntityOwnership";

/** Relationship types shown as top-level Connections rows (resource only). */
export const PRIMARY_CONNECTION_TYPES = [
  NOTIFY_TYPE_RECORD_KEYS.assignment,
  NOTIFY_TYPE_RECORD_KEYS.incentive,
] as const;

/** Notify subscription categories under the Notify group / drill-in. */
export const NOTIFY_CONNECTION_TYPES = [
  NOTIFY_TYPE_RECORD_KEYS.general,
  NOTIFY_TYPE_RECORD_KEYS.notes,
  NOTIFY_TYPE_RECORD_KEYS.schedule,
  NOTIFY_TYPE_RECORD_KEYS.checklist,
  NOTIFY_TYPE_RECORD_KEYS.reminder,
  NOTIFY_TYPE_RECORD_KEYS.files,
] as const;

/** Resource top-level category card that opens the notify list. */
export const NOTIFY_CATEGORY_CONTENT = {
  icon: Bell,
  title: "Notify",
  description: (entityName: string) => `Notify settings for this ${entityName}`,
  color: "text-blue-500",
  iconBg: "#3b82f6",
  usersLabel: "Notify",
} as const;

// Notification type content configuration
// iconBg mirrors Entity Finder task swatches (solid fill)
export const NOTIFY_TYPE_COLUMN_CONTENT = {
  [NOTIFY_TYPE_RECORD_KEYS.general]: {
    icon: Mail,
    title: "General",
    description: (entityName: string) => `Notify when this ${entityName}'s Tag, Team, Category, or Phase is updated`,
    color: "text-blue-500",
    iconBg: "#3b82f6",
    usersLabel: "Notify",
  },
  [NOTIFY_TYPE_RECORD_KEYS.notes]: {
    icon: StickyNote,
    title: "Notes",
    description: (entityName: string) => `Notify when this ${entityName}'s notes are added or updated`,
    color: "text-purple-500",
    iconBg: "#a855f7",
    usersLabel: "Notify",
  },
  [NOTIFY_TYPE_RECORD_KEYS.schedule]: {
    icon: CalendarDays,
    title: "Schedule",
    description: (entityName: string) => `Notify when this ${entityName} is scheduled or rescheduled`,
    color: "text-green-500",
    iconBg: "#22c55e",
    usersLabel: "Notify",
  },
  [NOTIFY_TYPE_RECORD_KEYS.checklist]: {
    icon: ListChecks,
    title: "Checklists",
    description: (entityName: string) => `Notify when this ${entityName}'s checklists are updated`,
    color: "text-orange-500",
    iconBg: "#f97316",
    usersLabel: "Notify",
  },
  [NOTIFY_TYPE_RECORD_KEYS.reminder]: {
    icon: AlarmClock,
    title: "Reminders",
    description: (entityName: string) => `Notify when this ${entityName}'s reminders are triggered`,
    color: "text-red-500",
    iconBg: "#ef4444",
    usersLabel: "Notify",
  },
  [NOTIFY_TYPE_RECORD_KEYS.files]: {
    icon: File,
    title: "Files",
    description: (entityName: string) => `Notify when this ${entityName}'s files are added, updated, or removed`,
    color: "text-indigo-500",
    iconBg: "#6366f1",
    usersLabel: "Notify",
  },
  [NOTIFY_TYPE_RECORD_KEYS.incentive]: {
    icon: Award,
    title: "Points eligible",
    description: (entityName: string) => `View and earn this ${entityName}'s points`,
    color: "text-yellow-500",
    iconBg: "#eab308",
    usersLabel: "Eligible",
  },
  [NOTIFY_TYPE_RECORD_KEYS.assignment]: {
    icon: UserIcon,
    title: "Assigned to",
    description: (entityName: string) => `View this ${entityName}'s assignments on Swivu's Workforce application`,
    color: "text-cyan-500",
    iconBg: "#06b6d4",
    usersLabel: "Assigned",
  },
};

// Exclude types by entity
// Note: Resources support all notification types (including assignment and incentive)
// Tasks and Clients exclude assignment and incentive types
export const EXCLUDE_NOTIFY_TYPE_BY_ENTITY_TYPE: Record<string, string[]> = {
  clientId: [NOTIFY_TYPE_RECORD_KEYS.assignment, NOTIFY_TYPE_RECORD_KEYS.incentive],
  taskId: [NOTIFY_TYPE_RECORD_KEYS.assignment, NOTIFY_TYPE_RECORD_KEYS.incentive],
  resourceId: EMPTY_EXCLUDE_TYPES, // Resources support all notification types
};
